import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { serviceUris } from '../../../../environments/services';
import { PackListFiltersModel } from '../models/pack-list-filters.model';
import { PaginatedPackListDetailsResponse, PaginatedPackListResponse } from '../models/paginated-pack-list.model';
import { PackListPdfRequest, PackListReport } from '../models/pack-list.model';

@Injectable({
  providedIn: 'root'
})
export class PackListService {
  private http = inject(HttpClient);
  private paginationState = {
    pageSize: 10,
    currentPage: 1
  };
  private filterState: Record<string, any> | null = null;

  searchPackLists(filters: PackListFiltersModel, pageNumber: number, pageSize: number): Observable<PaginatedPackListResponse> {
    const filteredParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value != null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any);

    const params: any = {
      ...filteredParams,
      page: pageNumber,
      pageSize
    };
    const url = `${environment.backOfficeUrl}${serviceUris.searchPackList}`;
    return this.http.get<PaginatedPackListResponse>(url, { params: params });
  }

  searchPackListDetails(
    packListFk: string,
    pageNumber: number,
    pageSize: number,
    sortBy?: string,
    sortDir?: string
  ): Observable<PaginatedPackListDetailsResponse> {
    const params: any = {
      packListFk,
      page: pageNumber,
      pageSize
    };
    if (sortBy && sortDir) {
      params.sortBy = sortBy;
      params.sortDir = sortDir;
    }
    const url = `${environment.backOfficeUrl}${serviceUris.searchPackListDetails}`;
    return this.http.get<PaginatedPackListDetailsResponse>(url, { params: params });
  }

  deletePackList(packListFk: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${serviceUris.deletePackList}`;
    return this.http.put<void>(url + `?packListFk=${packListFk}`, {});
  }

  getPackListDetails(request: PackListPdfRequest): Observable<PackListReport> {
    const url = `${environment.backOfficeUrl}${serviceUris.getPackListDetails}`;
    return this.http.post<any>(url, request).pipe(map((response) => response?.data ?? response));
  }

  getPaginationState(): { pageSize: number; currentPage: number } {
    return { ...this.paginationState };
  }

  setPaginationState(pageSize: number, currentPage: number): void {
    this.paginationState = { pageSize, currentPage };
  }

  setPageSize(pageSize: number): void {
    this.paginationState.pageSize = pageSize;
  }

  setCurrentPage(currentPage: number): void {
    this.paginationState.currentPage = currentPage;
  }

  clearPaginationState(): void {
    this.paginationState = {
      pageSize: 10,
      currentPage: 1
    };
  }

  getFilterState(): Record<string, any> | null {
    return this.filterState ? { ...this.filterState } : null;
  }

  setFilterState(filters: Record<string, any>): void {
    this.filterState = { ...filters };
  }

  clearFilterState(): void {
    this.filterState = null;
  }
}
